import React from 'react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { fdiDetailData } from '../../../data/fdiData';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
} from 'recharts';
import { getTooltipStyle } from '../../../hooks/useExpandedStyles';
import { Briefcase, TrendingUp, Users, Calendar } from 'lucide-react';

const getStatusStyle = (status: string): string => {
  const styles: Record<string, string> = {
    'ANNOUNCED': 'bg-data-amber/20 text-data-amber',
    'CONSTRUCTION': 'bg-data-green/20 text-data-green',
    'NEGOTIATION': 'bg-data-blue/20 text-data-blue',
    'PLANNING': 'bg-gray-600/30 text-gray-400',
    'MOU SIGNED': 'bg-cyan-500/20 text-cyan-400',
    'APPROVED': 'bg-emerald-500/20 text-emerald-400',
  };
  return styles[status] || 'bg-gray-600/30 text-gray-400';
};

const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    'ANNOUNCED': '#ffb800',
    'CONSTRUCTION': '#00d26a',
    'NEGOTIATION': '#00a3ff',
    'PLANNING': '#6b7280',
    'MOU SIGNED': '#06b6d4',
    'APPROVED': '#10b981',
  };
  return colors[status] || '#6b7280';
};

const SECTOR_COLORS: Record<string, string> = {
  'Energy': '#ffb800',
  'Manufacturing': '#00a3ff',
  'Mining': '#a855f7',
  'Automotive': '#ec4899',
  'Chemicals': '#06b6d4',
  'Technology': '#00d26a',
};

interface PipelinePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
  isExpanded?: boolean;
}

export const PipelinePanel: React.FC<PipelinePanelProps> = ({
  isMinimized,
  onMinimize,
  isExpanded = false,
}) => {
  const { pipeline, keyMetrics } = fdiDetailData;
  const sortedPipeline = [...pipeline].sort((a, b) => b.value - a.value);
  const totalPipeline = pipeline.reduce((sum, p) => sum + p.value, 0);

  // Group by status
  const byStatus = pipeline.reduce((acc, deal) => {
    acc[deal.status] = (acc[deal.status] || 0) + deal.value;
    return acc;
  }, {} as Record<string, number>);

  const statusData = Object.entries(byStatus)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Group by sector
  const bySector = pipeline.reduce((acc, deal) => {
    acc[deal.sector] = (acc[deal.sector] || 0) + deal.value;
    return acc;
  }, {} as Record<string, number>);

  const sectorData = Object.entries(bySector)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // Group by year
  const byYear = pipeline.reduce((acc, deal) => {
    const year = deal.year.toString();
    acc[year] = (acc[year] || 0) + deal.value;
    return acc;
  }, {} as Record<string, number>);

  const yearData = Object.entries(byYear)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const COLORS = ['#00d26a', '#00a3ff', '#ffb800', '#a855f7', '#ec4899', '#06b6d4'];

  const expandedContent = (
    <div className="flex flex-col h-full gap-8">
      {/* Top Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="flex items-center gap-2 text-gray-500 text-sm uppercase mb-2">
            <Briefcase size={14} />
            <span>Total Pipeline</span>
          </div>
          <div className="text-3xl font-bold text-data-amber">${totalPipeline.toFixed(1)}B</div>
          <div className="text-xs text-gray-500 mt-2">Announced & planned</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="flex items-center gap-2 text-gray-500 text-sm uppercase mb-2">
            <TrendingUp size={14} />
            <span>Greenfield</span>
          </div>
          <div className="text-3xl font-bold text-data-green">{keyMetrics.greenfield}%</div>
          <div className="text-xs text-gray-500 mt-2">New projects</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="flex items-center gap-2 text-gray-500 text-sm uppercase mb-2">
            <Calendar size={14} />
            <span>M&A Deals</span>
          </div>
          <div className="text-3xl font-bold text-data-blue">{keyMetrics.ma}%</div>
          <div className="text-xs text-gray-500 mt-2">Acquisitions</div>
        </div>
        <div className="bg-terminal-bg rounded-lg p-5">
          <div className="flex items-center gap-2 text-gray-500 text-sm uppercase mb-2">
            <Users size={14} />
            <span>Jobs Expected</span>
          </div>
          <div className="text-3xl font-bold text-white">{keyMetrics.jobsCreated.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-2">Direct employment</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-6">
        {/* Pipeline by Status */}
        <div className="bg-terminal-bg rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
            Pipeline by Status
          </h3>
          <div className="h-[300px] relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={110}
                  paddingAngle={2}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={index} fill={getStatusColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Value']}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-gray-500">TOTAL</div>
                <div className="text-2xl font-bold text-white">${totalPipeline.toFixed(1)}B</div>
              </div>
            </div>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {statusData.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: getStatusColor(entry.name) }}
                />
                <span className="text-sm text-gray-400">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline by Sector */}
        <div className="bg-terminal-bg rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
            Pipeline by Sector (USD Billions)
          </h3>
          <div className="h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sectorData}
                layout="vertical"
                margin={{ top: 10, right: 30, bottom: 10, left: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickFormatter={(v) => `$${v}B`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  width={70}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Pipeline Value']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {sectorData.map((entry, index) => (
                    <Cell
                      key={index}
                      fill={SECTOR_COLORS[entry.name] || COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Detailed Pipeline Table */}
      <div className="bg-terminal-bg rounded-lg p-6">
        <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
          Project Pipeline Detail
        </h3>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 uppercase text-xs tracking-wide border-b border-terminal-border">
                <th className="text-left py-3 px-4 font-medium">Project</th>
                <th className="text-left py-3 px-4 font-medium">Investor</th>
                <th className="text-left py-3 px-4 font-medium">Sector</th>
                <th className="text-right py-3 px-4 font-medium">Value</th>
                <th className="text-center py-3 px-4 font-medium">Status</th>
                <th className="text-center py-3 px-4 font-medium">Year</th>
                <th className="text-left py-3 px-4 font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {sortedPipeline.map((deal, index) => (
                <tr
                  key={deal.project}
                  className="border-b border-terminal-border/30 hover:bg-terminal-panel/50 transition-colors"
                >
                  <td className="py-3 px-4 text-white font-semibold">{deal.project}</td>
                  <td className="py-3 px-4 text-gray-400">{deal.investor}</td>
                  <td className="py-3 px-4 text-gray-400">{deal.sector}</td>
                  <td className="py-3 px-4 text-right text-data-green font-semibold">
                    ${deal.value}B
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusStyle(deal.status)}`}>
                      {deal.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-gray-400">{deal.year}</td>
                  <td className="py-3 px-4">
                    <div className="w-24 h-2 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${(deal.value / totalPipeline) * 100}%`,
                          backgroundColor: getStatusColor(deal.status),
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Timeline / Year Distribution */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-terminal-bg rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
            Pipeline by Expected Year
          </h3>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e1e26" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  tickFormatter={(v) => `$${v}B`}
                />
                <Tooltip
                  contentStyle={getTooltipStyle(true)}
                  formatter={(value: number) => [`$${value.toFixed(2)}B`, 'Pipeline']}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {yearData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Key Insights */}
        <div className="bg-terminal-bg rounded-lg p-6">
          <h3 className="text-base font-semibold text-gray-200 uppercase tracking-wide mb-4">
            Pipeline Highlights
          </h3>
          <ul className="space-y-3 text-sm text-gray-400">
            <li className="flex items-start gap-2">
              <span className="text-data-amber">•</span>
              Largest deal: {sortedPipeline[0]?.project} (${sortedPipeline[0]?.value}B)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-data-green">•</span>
              {pipeline.filter((p) => p.status === 'CONSTRUCTION').length} projects under construction
            </li>
            <li className="flex items-start gap-2">
              <span className="text-data-blue">•</span>
              Energy sector leads with ${bySector['Energy']?.toFixed(1) || 0}B pipeline
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gray-500">•</span>
              {pipeline.filter((p) => p.year === 2025).length} projects expected in 2025
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const compactContent = (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto custom-scrollbar">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-terminal-panel">
            <tr className="text-gray-500 border-b border-terminal-border">
              <th className="text-left py-1.5 px-2 font-medium">PROJECT</th>
              <th className="text-left py-1.5 px-2 font-medium">INVESTOR</th>
              <th className="text-left py-1.5 px-2 font-medium">SECTOR</th>
              <th className="text-right py-1.5 px-2 font-medium">VALUE</th>
              <th className="text-center py-1.5 px-2 font-medium">STATUS</th>
              <th className="text-center py-1.5 px-2 font-medium">YEAR</th>
            </tr>
          </thead>
          <tbody>
            {sortedPipeline.map((deal) => (
              <tr
                key={deal.project}
                className="border-b border-terminal-border/30 hover:bg-data-amber/5 transition-colors"
              >
                <td className="py-1.5 px-2 text-white font-medium">{deal.project}</td>
                <td className="py-1.5 px-2 text-gray-400">{deal.investor}</td>
                <td className="py-1.5 px-2 text-gray-400">{deal.sector}</td>
                <td className="py-1.5 px-2 text-right text-data-green font-medium">${deal.value}B</td>
                <td className="py-1.5 px-2 text-center">
                  <span className={`px-1.5 py-1 rounded text-xs font-medium ${getStatusStyle(deal.status)}`}>
                    {deal.status}
                  </span>
                </td>
                <td className="py-1.5 px-2 text-center text-gray-400">{deal.year}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-2 border-t border-terminal-border/50 flex items-center justify-between">
        <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
        <span className="text-xs text-gray-500">Dec 2025</span>
      </div>
    </div>
  );

  if (isExpanded) {
    return expandedContent;
  }

  return (
    <Panel
      title="INVESTMENT PIPELINE"
      subtitle="ANNOUNCED & PLANNED DEALS"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      expandedContent={expandedContent}
    >
      {compactContent}
    </Panel>
  );
};

export default PipelinePanel;
